/**
 * API Service
 * Cliente HTTP para comunicação com o backend
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { DEFAULT_API_BASE_URL } from '../config/constants';

const PRODUCT_ID_STORAGE_KEY = 'vover_admin_product_id';
const MULTI_PRODUCT_IDS_STORAGE_KEY = 'vover_admin_product_ids';

function readMultiProductIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(MULTI_PRODUCT_IDS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x: unknown) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function mergeProductParams(
  _url: string | undefined,
  params?: Record<string, unknown>
): Record<string, unknown> {
  const base = { ...(params || {}) };
  if (typeof window === 'undefined') return base;

  const path = window.location.pathname;
  const search = window.location.search;

  if (path.startsWith('/configurations')) {
    return base;
  }

  const multiPaths = ['/', '/audit-logs', '/financial-flow'];
  const isMulti = path === '/' || multiPaths.some(p => p !== '/' && path.startsWith(p));

  if (isMulti) {
    const ids = readMultiProductIds();
    if (ids.length) {
      return { ...base, productIds: ids.join(',') };
    }
    return base;
  }

  if (path.startsWith('/platform/products')) {
    const tab = new URLSearchParams(search).get('tab') || 'registry';
    const scopedTabs = [
      'tenants',
      'pricing',
      'terms',
      'trial',
      'billing',
      'email',
      'systemadmins',
      'videos'
    ];
    if (scopedTabs.includes(tab)) {
      const stored = localStorage.getItem(PRODUCT_ID_STORAGE_KEY)?.trim();
      if (stored) return { ...base, productId: stored };
      return base;
    }
    return base;
  }

  // single-all: vazio no filtro = todos os produtos — não reutilizar localStorage se a página não enviou productId
  const singleAllPaths = ['/nfse-observability', '/billing-metrics', '/impersonation'];
  if (singleAllPaths.some(p => path.startsWith(p))) {
    if (Object.prototype.hasOwnProperty.call(base, 'productId')) {
      const trimmed = String(base.productId ?? '').trim();
      if (trimmed) return { ...base, productId: trimmed };
      const { productId: _omit, ...rest } = base;
      return rest;
    }
    return base;
  }

  const stored = localStorage.getItem(PRODUCT_ID_STORAGE_KEY)?.trim();
  if (stored) return { ...base, productId: stored };
  return base;
}

// URL base do backend na raiz (sem /api/admin). Remove /api/admin se vier no env (compatibilidade).
const rawBase = process.env.REACT_APP_ADMIN_API_URL || DEFAULT_API_BASE_URL;
const API_BASE_URL = rawBase.replace(/\/api\/admin\/?$/, '') || DEFAULT_API_BASE_URL;

/** Uma única tentativa de refresh por vez (vários 401 em paralelo compartilham a mesma Promise). */
let refreshSessionPromise: Promise<boolean> | null = null;

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const skipAppId =
          config.url?.includes('/platform/products') ||
          config.url?.includes('/auth/') ||
          (typeof window !== 'undefined' &&
            window.location.pathname.startsWith('/configurations') &&
            (config.url?.includes('/billing/config') ||
              config.url?.includes('/system-config') ||
              config.url?.includes('/videos')));
        if (!skipAppId) {
          const params = mergeProductParams(
            config.url,
            config.params as Record<string, unknown> | undefined
          );
          config.params = params;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor
    this.api.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Evitar loops infinitos
        if (!originalRequest || originalRequest._retry) {
          return Promise.reject(error);
        }

        // Se for 401 e não for uma requisição de refresh ou login
        if (
          error.response?.status === 401 &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/login') &&
          !originalRequest.url?.includes('/auth/verify-2fa')
        ) {
          originalRequest._retry = true;

          try {
            if (!refreshSessionPromise) {
              refreshSessionPromise = this.api
                .post('/auth/refresh')
                .then(res => Boolean((res.data as { success?: boolean })?.success))
                .catch(() => false)
                .finally(() => {
                  refreshSessionPromise = null;
                });
            }
            const refreshed = await refreshSessionPromise;

            if (refreshed) {
              return this.api(originalRequest);
            }
            // Refresh falhou → sinalizar para o app limpar sessão
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('vover-admin:auth-expired'));
            }
          } catch {
            // refresh já tratado em refreshSessionPromise
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(email: string, password: string) {
    return this.api.post('/auth/login', { email, password });
  }

  async verify2FA(tempToken: string, code: string, method: 'totp' | 'email') {
    return this.api.post('/auth/verify-2fa', { tempToken, code, method });
  }

  async logout() {
    return this.api.post('/auth/logout');
  }

  async getMe() {
    return this.api.get('/auth/me');
  }

  async refreshToken() {
    return this.api.post('/auth/refresh');
  }

  // Dashboard
  async getDashboardStats(productIds?: string[]) {
    return this.api.get('/dashboard/stats', {
      params: productIds?.length ? { productIds: productIds.join(',') } : undefined
    });
  }

  async getActionsHistory(days: number = 30, productIds?: string[]) {
    return this.api.get('/dashboard/actions-history', {
      params: { days, ...(productIds?.length ? { productIds: productIds.join(',') } : {}) }
    });
  }

  async getActionsByType(limit: number = 10, productIds?: string[]) {
    return this.api.get('/dashboard/actions-by-type', {
      params: { limit, ...(productIds?.length ? { productIds: productIds.join(',') } : {}) }
    });
  }

  async getUniqueUsersHistory(days: number = 30, productIds?: string[]) {
    return this.api.get('/dashboard/unique-users-history', {
      params: { days, ...(productIds?.length ? { productIds: productIds.join(',') } : {}) }
    });
  }

  async getPeriodComparison(days: number = 7, productIds?: string[]) {
    return this.api.get('/dashboard/period-comparison', {
      params: { days, ...(productIds?.length ? { productIds: productIds.join(',') } : {}) }
    });
  }

  // Tenants
  /** Tenants de teste Google Play + Apple (idempotente; revisao.google / revisao.apple @vover.com.br) */
  async createMobileTestTenants(productId: string) {
    return this.api.post('/tenants/create-test', null, { params: { productId } });
  }

  async getTenants(params?: any) {
    return this.api.get('/tenants', { params });
  }

  async getTenant(tenantId: string, params?: any) {
    return this.api.get(`/tenants/${tenantId}`, { params });
  }

  async getTenantStats(tenantId: string) {
    return this.api.get(`/tenants/${tenantId}/stats`);
  }

  async updateTenant(tenantId: string, data: any) {
    return this.api.put(`/tenants/${tenantId}`, data);
  }

  async activateTenant(tenantId: string) {
    return this.api.post(`/tenants/${tenantId}/activate`);
  }

  async deactivateTenant(tenantId: string) {
    return this.api.post(`/tenants/${tenantId}/deactivate`);
  }

  /** subscriptionActive no control — mesmo critério do vover-app (inadimplência) */
  async patchTenantSubscriptionBilling(tenantId: string, subscriptionActive: boolean) {
    return this.api.patch(`/tenants/${encodeURIComponent(tenantId)}/subscription-billing`, {
      subscriptionActive
    });
  }

  /** is_test no control — revisão de loja / sem paywall trial-fatura no vover-app */
  async patchTenantIsTest(tenantId: string, is_test: boolean) {
    return this.api.patch(`/tenants/${encodeURIComponent(tenantId)}/is-test`, { is_test });
  }

  /** isPartner no control — cliente parceiro sem bloqueio de acesso (vover-ibody) */
  async patchTenantIsPartner(tenantId: string, isPartner: boolean) {
    return this.api.patch(`/tenants/${encodeURIComponent(tenantId)}/is-partner`, { isPartner });
  }

  // Audit Logs
  async getAuditLogs(params?: any) {
    return this.api.get('/audit-logs', { params });
  }

  async exportAuditLogs(params?: any) {
    return this.api.get('/audit-logs/export', {
      params,
      responseType: 'blob'
    });
  }

  async getAuditLogStats(params?: Record<string, string>) {
    return this.api.get('/audit-logs/stats', { params });
  }

  async getBillingNotificationAudits(params?: Record<string, string | number | undefined>) {
    return this.api.get('/audit-logs/billing-notifications', { params });
  }

  async getBillingNotificationAuditStats(params?: Record<string, string>) {
    return this.api.get('/audit-logs/billing-notifications/stats', { params });
  }

  async exportBillingNotificationAudits(params?: Record<string, string | undefined>) {
    return this.api.get('/audit-logs/billing-notifications/export', {
      params,
      responseType: 'blob'
    });
  }

  async getBillingNotificationUpcoming(params?: Record<string, string | number | undefined>) {
    return this.api.get('/audit-logs/billing-notifications/upcoming', { params });
  }

  async dispatchBillingNotification(body: {
    mode?: string;
    tenantId?: string;
    event: string;
    paymentId?: string;
    invoiceId?: string;
    channels?: string[];
  }) {
    return this.api.post('/audit-logs/billing-notifications/dispatch', body);
  }

  // Support Config (modal de funcionalidade em desenvolvimento)
  async getSupportConfig() {
    return this.api.get('/support-config');
  }

  async updateSupportConfig(data: any) {
    return this.api.put('/support-config', data);
  }

  // Product Email Config (por produto — API key e domínio próprios em v_<productId>_core)
  async getProductEmailConfig() {
    return this.api.get('/product-email-config/config');
  }

  async updateProductEmailConfig(data: any) {
    return this.api.put('/product-email-config/config', data);
  }

  async testProductEmail(to: string, replyTo?: string, attachment?: File) {
    if (attachment) {
      const formData = new FormData();
      formData.append('to', to);
      if (replyTo) formData.append('replyTo', replyTo);
      formData.append('attachment', attachment);
      return this.api.post('/product-email-config/config/test', formData, {
        headers: { 'Content-Type': undefined } as any
      });
    }
    return this.api.post('/product-email-config/config/test', { to, replyTo });
  }

  // System Config (geral do sistema)
  async getSystemConfig() {
    return this.api.get('/system-config');
  }

  async updateSystemConfig(data: any) {
    return this.api.put('/system-config', data);
  }

  // Mongo Backup (cron diário → R2, restrito a super_admin)
  async getMongoBackupConfigs() {
    return this.api.get('/mongo-backup');
  }

  async createMongoBackupConfig(data: any) {
    return this.api.post('/mongo-backup', data);
  }

  async updateMongoBackupConfig(id: string, data: any) {
    return this.api.put(`/mongo-backup/${id}`, data);
  }

  async deleteMongoBackupConfig(id: string) {
    return this.api.delete(`/mongo-backup/${id}`);
  }

  async runMongoBackupNow(id: string) {
    return this.api.post(`/mongo-backup/${id}/run`);
  }

  async getMongoBackupRuns(id: string, limit = 20) {
    return this.api.get(`/mongo-backup/${id}/runs`, { params: { limit } });
  }

  async getMongoBackupRunLog(id: string, runId: string) {
    return this.api.get(`/mongo-backup/${id}/runs/${runId}/log`);
  }

  // Impersonation
  async getSessions(params?: any) {
    return this.api.get('/impersonation/sessions', { params });
  }

  async terminateSession(sessionId: string) {
    return this.api.post(`/impersonation/sessions/${sessionId}/terminate`);
  }

  // Pricing
  async getPricingConfig() {
    return this.api.get('/pricing/config');
  }

  async updatePricingConfig(data: any) {
    return this.api.put('/pricing/config', data);
  }

  async updateTenantPricingConfig(tenantId: string, data: any) {
    return this.api.put(`/pricing/config/tenant/${tenantId}`, data);
  }

  async removeTenantPricingConfig(tenantId: string) {
    return this.api.delete(`/pricing/config/tenant/${tenantId}`);
  }

  async getIbodyPlans() {
    return this.api.get('/pricing/config/ibody-plans');
  }

  async updateIbodyPlans(plans: any[]) {
    return this.api.put('/pricing/config/ibody-plans', { plans });
  }

  async getEntryOffers() {
    return this.api.get('/pricing/config/offers');
  }

  async createEntryOffer(data: any) {
    return this.api.post('/pricing/config/offers', data);
  }

  async updateEntryOffer(key: string, data: any) {
    return this.api.put(`/pricing/config/offers/${encodeURIComponent(key)}`, data);
  }

  async closeEntryOfferSignup(key: string) {
    return this.api.post(`/pricing/config/offers/${encodeURIComponent(key)}/close-signup`);
  }

  async previewEntryOfferReprice(key: string, fromPeriod: string) {
    return this.api.post(`/pricing/config/offers/${encodeURIComponent(key)}/reprice-preview`, {
      fromPeriod
    });
  }

  async repriceEntryOffer(key: string, fromPeriod: string) {
    return this.api.post(`/pricing/config/offers/${encodeURIComponent(key)}/reprice`, {
      fromPeriod
    });
  }

  async resendEntryOfferRepriceNotices(key: string, fromPeriod: string) {
    return this.api.post(`/pricing/config/offers/${encodeURIComponent(key)}/reprice-notices`, {
      fromPeriod
    });
  }

  async getFeatureCatalog() {
    return this.api.get('/pricing/config/features');
  }

  async saveFeatureCatalog(features: Array<{ key: string; label: string; description?: string }>) {
    return this.api.put('/pricing/config/features', { features });
  }

  async getTenantPricingSchedule(tenantId: string, months = 12) {
    return this.api.get(`/pricing/tenants/${encodeURIComponent(tenantId)}/schedule`, {
      params: { months }
    });
  }

  async addTenantNegotiatedPrice(
    tenantId: string,
    data: { amount: number; fromPeriod: string; untilPeriod: string | null; reason: string }
  ) {
    return this.api.post(`/pricing/tenants/${encodeURIComponent(tenantId)}/negotiated`, data);
  }

  async endTenantNegotiatedPrice(tenantId: string, segmentId: string, untilPeriod: string) {
    return this.api.post(
      `/pricing/tenants/${encodeURIComponent(tenantId)}/negotiated/${encodeURIComponent(segmentId)}/end`,
      { untilPeriod }
    );
  }

  async setTenantEntitlements(tenantId: string, data: { add: string[]; remove: string[] }) {
    return this.api.put(`/pricing/tenants/${encodeURIComponent(tenantId)}/entitlements`, data);
  }

  // Terms
  async getTermDocuments(params?: any) {
    return this.api.get('/terms/documents', { params });
  }

  async createTermDocument(data: any) {
    return this.api.post('/terms/documents', data);
  }

  async updateTermDocument(id: string, data: any) {
    return this.api.put(`/terms/documents/${id}`, data);
  }

  async deactivateTermDocument(id: string) {
    return this.api.post(`/terms/documents/${id}/deactivate`);
  }

  // Trial Period
  async getTrialPeriodConfig() {
    return this.api.get('/trial-period/config');
  }

  async updateDefaultTrialPeriod(days: number) {
    return this.api.put('/trial-period/config/default', { days });
  }

  async updateTenantTrialPeriod(tenantId: string, days: number) {
    return this.api.put(`/trial-period/config/tenant/${encodeURIComponent(tenantId)}`, { days });
  }

  async removeTenantTrialPeriod(tenantId: string) {
    return this.api.delete(`/trial-period/config/tenant/${encodeURIComponent(tenantId)}`);
  }

  // System Admin Users (por produto — login sem tenant, usado para impersonação de suporte)
  async listSystemAdminUsers() {
    return this.api.get('/system-admin-users');
  }

  async createSystemAdminUser(data: { email: string; password: string }) {
    return this.api.post('/system-admin-users', data);
  }

  async setSystemAdminUserActive(userId: string, isActive: boolean) {
    return this.api.patch(`/system-admin-users/${encodeURIComponent(userId)}/active`, {
      isActive
    });
  }

  async resetSystemAdminUserPassword(userId: string, password: string) {
    return this.api.post(`/system-admin-users/${encodeURIComponent(userId)}/reset-password`, {
      password
    });
  }

  async deleteSystemAdminUser(userId: string) {
    return this.api.delete(`/system-admin-users/${encodeURIComponent(userId)}`);
  }

  // Users
  async getUsers(params?: any) {
    return this.api.get('/users', { params });
  }

  async resetUserPassword(userId: string, data: any) {
    return this.api.post(`/users/${userId}/reset-password`, data);
  }

  /** Ativar ou desativar usuário. data: { tenantId, isActive } onde isActive é o novo estado desejado. */
  async toggleUserStatus(userId: string, data: { tenantId: string; isActive: boolean }) {
    const { tenantId, isActive } = data;
    const endpoint = isActive ? 'activate' : 'deactivate';
    return this.api.post(`/users/${userId}/${endpoint}`, { tenantId });
  }

  // Admin users (VOVER ADMIN, collection admin_users) — apenas super_admin
  async getAdminUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    role?: string;
  }) {
    return this.api.get('/admin-users', { params });
  }

  async getAdminUser(id: string) {
    return this.api.get(`/admin-users/${id}`);
  }

  async createAdminUser(data: {
    email: string;
    password: string;
    name: string;
    role?: string;
    productScopes?: string[];
  }) {
    return this.api.post('/admin-users', data);
  }

  async updateAdminUser(
    id: string,
    data: { name?: string; role?: string; password?: string; productScopes?: string[] }
  ) {
    return this.api.put(`/admin-users/${id}`, data);
  }

  async setAdminUserActive(id: string, isActive: boolean) {
    return this.api.patch(`/admin-users/${id}/active`, { isActive });
  }

  // Videos (R2 - public | private/tutorials | private/downloads)
  async getVideosStatus() {
    return this.api.get('/videos/status');
  }

  async listVideos(
    folder: 'public' | 'private-tutorials' | 'private-downloads',
    productId?: string
  ) {
    const params: Record<string, string> = { folder };
    if (productId?.trim()) params.productId = productId.trim();
    return this.api.get('/videos', { params });
  }

  async uploadVideo(
    file: File,
    folder: 'public' | 'private-tutorials' | 'private-downloads',
    productId?: string
  ) {
    const formData = new FormData();
    formData.append('video', file);
    formData.append('folder', folder);
    if (productId?.trim()) formData.append('productId', productId.trim());
    return this.api.post('/videos/upload', formData, {
      headers: { 'Content-Type': undefined } as any
    });
  }

  async deleteVideo(key: string) {
    return this.api.delete('/videos', { params: { key } });
  }

  async renameVideo(key: string, name: string) {
    return this.api.patch('/videos/rename', { key, name });
  }

  async getVideoPreviewUrl(key: string) {
    return this.api.get('/videos/preview', { params: { key } });
  }

  // Financial Flow
  async getFinancialDashboard(year: number, month?: number) {
    const params: Record<string, number> = { year };
    if (month != null) params.month = month;
    return this.api.get('/financial-flow/dashboard', { params });
  }

  async getFinancialSummary(year: number, month?: number) {
    const params: Record<string, number> = { year };
    if (month != null) params.month = month;
    return this.api.get('/financial-flow/summary', { params });
  }

  async listFinancialExpenses(year: number, month?: number) {
    const params: Record<string, number> = { year };
    if (month != null) params.month = month;
    return this.api.get('/financial-flow/expenses', { params });
  }

  async adjustExpenseEntry(id: string, amountBRL: number) {
    return this.api.patch(`/financial-flow/expenses/entries/${id}`, { amountBRL });
  }

  async processExpenseEntries(
    year: number,
    startMonth: number,
    recurringExpenseIds: string[],
    amountOverrides?: Record<string, number>
  ) {
    return this.api.post('/financial-flow/expenses/process', {
      year,
      startMonth,
      recurringExpenseIds,
      ...(amountOverrides && Object.keys(amountOverrides).length > 0 ? { amountOverrides } : {})
    });
  }

  async markExpensePaid(id: string, paid: boolean, paidAt?: string) {
    const body: { paid: boolean; paidAt?: string } = { paid };
    if (paid && paidAt) body.paidAt = paidAt;
    return this.api.patch(`/financial-flow/expenses/entries/${id}/paid`, body);
  }

  async createManualExpense(
    year: number,
    month: number,
    data: { description: string; amountBRL: number; day?: number; recurringExpenseId?: string }
  ) {
    const body: Record<string, unknown> = {
      year,
      month,
      day: data.day,
      description: data.description,
      amountBRL: data.amountBRL
    };
    if (data.recurringExpenseId?.trim()) {
      body.recurringExpenseId = data.recurringExpenseId.trim();
    }
    return this.api.post('/financial-flow/expenses/manual', body);
  }

  async deleteExpenseEntry(id: string) {
    return this.api.delete(`/financial-flow/expenses/entries/${id}`);
  }

  async listRecurringExpenses(activeOnly?: boolean) {
    return this.api.get('/financial-flow/recurring-expenses', {
      params: activeOnly ? { activeOnly: 'true' } : {}
    });
  }

  async createRecurringExpense(data: {
    description: string;
    defaultAmountBRL: number;
    dueDayOfMonth: number;
    recurrencePeriod?: 'monthly' | 'quarterly' | 'semiannual' | 'annual';
    recurrenceStartDate?: string;
  }) {
    return this.api.post('/financial-flow/recurring-expenses', data);
  }

  async updateRecurringExpense(
    id: string,
    data: {
      description?: string;
      defaultAmountBRL?: number;
      dueDayOfMonth?: number;
      recurrencePeriod?: 'monthly' | 'quarterly' | 'semiannual' | 'annual';
      recurrenceStartDate?: string;
      isActive?: boolean;
    }
  ) {
    return this.api.put(`/financial-flow/recurring-expenses/${id}`, data);
  }

  async deleteRecurringExpense(id: string) {
    return this.api.delete(`/financial-flow/recurring-expenses/${id}`);
  }

  async getFinancialRevenues(year: number, month?: number) {
    const params: Record<string, number> = { year };
    if (month != null) params.month = month;
    return this.api.get('/financial-flow/revenues', { params });
  }

  // MRR snapshots / relatório anual
  async getMrrYearReport(year: number, includePerTenant = false) {
    return this.api.get('/financial-flow/year-report', {
      params: { year, includePerTenant: includePerTenant ? 'true' : 'false' }
    });
  }

  async closeMrrCompetence(competence: string, notes?: string) {
    return this.api.post(`/financial-flow/competences/${competence}/close`, { notes });
  }

  async reopenMrrCompetence(competence: string, notes?: string) {
    return this.api.post(`/financial-flow/competences/${competence}/reopen`, { notes });
  }

  async seedMrrCompetence(
    competence: string,
    payload: {
      estimatedMRR: number;
      activeTenants?: number;
      trialTenants?: number;
      byPackageType?: { condominio?: number; gestao?: number; institucional?: number };
      perTenant?: Array<{
        tenantId: string;
        tenantName: string;
        packageType: 'condominio' | 'gestao' | 'institucional';
        status: 'active' | 'trial';
        monthlyAmount: number;
        totalUnits?: number;
        totalClients?: number;
      }>;
      notes?: string;
    }
  ) {
    return this.api.post(`/financial-flow/competences/${competence}/seed`, payload);
  }

  async addExpenseInvoice(id: string, file: File, type: 'invoice' | 'payment_receipt' = 'invoice') {
    const formData = new FormData();
    formData.append('file', file);
    return this.api.post(`/financial-flow/expenses/entries/${id}/invoices`, formData, {
      params: { type },
      headers: { 'Content-Type': undefined } as any
    });
  }

  async removeExpenseInvoice(
    id: string,
    key: string,
    type: 'invoice' | 'payment_receipt' = 'invoice'
  ) {
    return this.api.delete(`/financial-flow/expenses/entries/${id}/invoices`, {
      params: { key, type }
    });
  }

  async getInvoicePreviewUrl(key: string) {
    const res = await this.api.get('/financial-flow/invoices/preview', {
      params: { key }
    });
    return res.data?.data?.url as string;
  }

  // Billing (vover-billing via proxy)
  async getBillingMetrics(from: string, to: string, productId?: string) {
    return this.api.get('/billing/metrics', { params: { from, to, productId } });
  }

  async listBillingInvoices(params: {
    from?: string;
    to?: string;
    paidFrom?: string;
    paidTo?: string;
    createdFrom?: string;
    createdTo?: string;
    dateField?: 'dueDate' | 'paidAt' | 'createdAt';
    billingPeriod?: string;
    billingPeriodPrefix?: string;
    tenantId?: string;
    productId?: string;
    limit?: number;
    starting_after?: string;
  }) {
    return this.api.get('/billing/invoices', { params });
  }

  async getBillingInvoice(id: string) {
    return this.api.get(`/billing/invoices/${encodeURIComponent(id)}`);
  }

  async updateBillingInvoice(id: string, data: Record<string, unknown>) {
    return this.api.put(`/billing/invoices/${encodeURIComponent(id)}`, data);
  }

  async refreshBillingInvoiceReceipt(id: string) {
    return this.api.post(`/billing/invoices/${encodeURIComponent(id)}/refresh-receipt`, {});
  }

  async issueBillingInvoiceNfse(id: string, body?: { serviceDescription?: string }) {
    return this.api.post(`/billing/invoices/${encodeURIComponent(id)}/issue-nfse`, body ?? {});
  }

  async syncBillingInvoiceNfseFromIpm(id: string, body?: { rpsNumero?: number }) {
    return this.api.post(`/billing/invoices/${encodeURIComponent(id)}/sync-nfse-ipm`, body ?? {});
  }

  async testBillingNfseNacionalConnection() {
    return this.api.post('/billing/nfse-nacional/test-connection', {});
  }

  async cancelBillingNfseNacional(
    externalId: string,
    body: { motivoCodigo: '1' | '2' | '9'; motivoTexto: string }
  ) {
    return this.api.post(`/billing/nfse/${encodeURIComponent(externalId)}/cancel`, body);
  }

  async reconcileBillingNfseNacional(externalId: string) {
    return this.api.post(`/billing/nfse/${encodeURIComponent(externalId)}/reconcile`, {});
  }

  async getBillingInvoiceNfsePayload(id: string) {
    return this.api.get(`/billing/invoices/${encodeURIComponent(id)}/nfse-payload`);
  }

  async listBillingPayments(params: Record<string, string | number | undefined>) {
    return this.api.get('/billing/payments', { params });
  }

  async listBillingNfse(params: Record<string, string | number | undefined>) {
    return this.api.get('/billing/nfse', { params });
  }

  async getBillingNfseAuditSummary(params: {
    from?: string;
    to?: string;
    tenantId?: string;
    productId?: string;
  }) {
    return this.api.get('/billing/nfse-audit/summary', { params });
  }

  async listBillingNfseAuditEvents(params: {
    from?: string;
    to?: string;
    tenantId?: string;
    productId?: string;
    outcome?: 'authorized' | 'rejected' | 'error';
    invoiceId?: string;
    paymentId?: string;
    limit?: number;
    starting_after?: string;
  }) {
    return this.api.get('/billing/nfse-audit/events', { params });
  }

  async getBillingNfseAuditTimeseries(params: {
    from?: string;
    to?: string;
    tenantId?: string;
    productId?: string;
  }) {
    return this.api.get('/billing/nfse-audit/timeseries', { params });
  }

  async getBillingNfseAuditReasons(params: {
    from?: string;
    to?: string;
    tenantId?: string;
    productId?: string;
    limit?: number;
  }) {
    return this.api.get('/billing/nfse-audit/reasons', { params });
  }

  async listBillingNfseAuditTenants(params: {
    from?: string;
    to?: string;
    limit?: number;
    productId?: string;
  }) {
    return this.api.get('/billing/nfse-audit/tenants', { params });
  }

  async getBillingGlobalConfig() {
    return this.api.get('/billing/config');
  }

  async updateBillingGlobalConfig(data: Record<string, unknown>) {
    return this.api.put('/billing/config', data);
  }

  // Inter webhooks (via vover-admin backend -> vover-billing admin)
  async getInterWebhook(tipoWebhook: 'pix-pagamento' | 'boleto-pagamento') {
    return this.api.get(`/billing/inter/webhooks/${encodeURIComponent(tipoWebhook)}`);
  }

  async putInterWebhook(
    tipoWebhook: 'pix-pagamento' | 'boleto-pagamento',
    data: { webhookUrl: string }
  ) {
    return this.api.put(`/billing/inter/webhooks/${encodeURIComponent(tipoWebhook)}`, data);
  }

  async deleteInterWebhook(tipoWebhook: 'pix-pagamento' | 'boleto-pagamento') {
    return this.api.delete(`/billing/inter/webhooks/${encodeURIComponent(tipoWebhook)}`);
  }

  async listInterWebhookCallbacks(
    tipoWebhook: 'pix-pagamento' | 'boleto-pagamento',
    params: {
      dataHoraInicio: string;
      dataHoraFim: string;
      // Cobrança (boleto): codigoSolicitacao, pagina, tamanhoPagina
      codigoSolicitacao?: string;
      pagina?: number;
      tamanhoPagina?: number;
      // Pix cobrança: txid, pagina, tamanhoPagina
      txid?: string;
    }
  ) {
    return this.api.get(`/billing/inter/webhooks/${encodeURIComponent(tipoWebhook)}/callbacks`, {
      params
    });
  }

  async retryInterWebhookCallbacks(
    tipoWebhook: 'pix-pagamento' | 'boleto-pagamento',
    data: { codigoSolicitacao: string[] }
  ) {
    return this.api.post(
      `/billing/inter/webhooks/${encodeURIComponent(tipoWebhook)}/callbacks/retry`,
      data
    );
  }

  /** Baixa / lançamento manual de receita realizada (vover-billing) */
  // Platform (control plane)
  async listProducts() {
    return this.api.get('/platform/products');
  }

  async upsertProduct(productId: string, body: Record<string, unknown>) {
    return this.api.put(`/platform/products/${encodeURIComponent(productId)}`, body);
  }

  async deleteProduct(productId: string) {
    return this.api.delete(`/platform/products/${encodeURIComponent(productId)}`);
  }

  async testProductConnection(productId: string) {
    return this.api.post(`/platform/products/${encodeURIComponent(productId)}/test-connection`);
  }

  async listBillingProducts() {
    return this.api.get('/platform/billing/products');
  }

  async listBillingCredentials(productId: string) {
    return this.api.get(`/platform/billing/products/${encodeURIComponent(productId)}/credentials`);
  }

  async createBillingCredential(productId: string, body: Record<string, unknown>) {
    return this.api.post(
      `/platform/billing/products/${encodeURIComponent(productId)}/credentials`,
      body
    );
  }

  async rotateBillingCredential(productId: string, body: Record<string, unknown>) {
    return this.api.post(
      `/platform/billing/products/${encodeURIComponent(productId)}/credentials/rotate`,
      body
    );
  }

  async revokeBillingCredential(productId: string, credentialId: string) {
    return this.api.post(
      `/platform/billing/products/${encodeURIComponent(productId)}/credentials/${encodeURIComponent(
        credentialId
      )}/revoke`,
      {}
    );
  }

  async deleteBillingCredential(productId: string, credentialId: string) {
    return this.api.delete(
      `/platform/billing/products/${encodeURIComponent(productId)}/credentials/${encodeURIComponent(
        credentialId
      )}`
    );
  }

  async getCrossProductDashboard(params?: { year?: number; month?: number }) {
    return this.api.get('/platform/dashboard/cross-product', { params });
  }

  async onboardProduct(body: Record<string, unknown>) {
    return this.api.post('/platform/products/onboard', body);
  }

  async createManualBillingInvoice(data: {
    productId?: string;
    tenantId: string;
    billingPeriod: string;
    amountCents?: number;
    amountBRL?: number;
    dueDate: string;
    status?: 'draft' | 'pending' | 'paid' | 'overdue' | 'cancelled';
    paidAt?: string;
    description?: string;
    note?: string;
  }) {
    const { productId, ...body } = data;
    return this.api.post('/billing/invoices/manual', body, {
      params: productId ? { productId } : undefined
    });
  }
}

export const apiService = new ApiService();
export default apiService;
